import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { ROUTES } from '../config/routes';
import { Navigation, type DashboardTab } from '../components/dashboard/Navigation';
import { DashboardOverview } from '../components/dashboard/DashboardOverview';
import { EmployeeDonutChart } from '../components/dashboard/EmployeeDonutChart';
import { DepartmentTestHistory } from '../components/dashboard/DepartmentTestHistory';
import { AnalyticsHeatmap } from '../components/dashboard/AnalyticsHeatmap';
import { AnalyticsResultsChart } from '../components/dashboard/AnalyticsResultsChart';
import CompanyUserList from '../components/CompanyUserList';
import TestHistory, { DEFAULT_REPORT_FILTERS, formatTestType, type ReportFilters } from '../components/TestHistory';
import UserProfile from '../components/UserProfile';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { toDailyAverages } from '../utils/dailyAverages';
import { getLastYearDateRange } from '../utils/dateRange';
import type { CompanyAnalytics, Test, TestResults, User } from '../types';

interface CompanyUser extends User {
  testCount: number;
  lastTestDate?: Date;
  averagePerformance: {
    reactionTime: number;
    lapseRate: number;
  };
}

const toDate = (value: Date | string | undefined) => (value ? new Date(value) : undefined);

const calculateMedian = (values: number[]) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const formatDate = (date: Date) =>
  date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const HISTORY_PAGE_SIZE = 25;

const AdminDashboard: React.FC = () => {
  const { user, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [companyAnalytics, setCompanyAnalytics] = useState<CompanyAnalytics | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [historyTests, setHistoryTests] = useState<Test[]>([]);
  const [trendTests, setTrendTests] = useState<Test[]>([]);
  const [companyName, setCompanyName] = useState('Company Dashboard');
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const [kioskToken, setKioskToken] = useState<string | null>(null);
  const [kioskTokenExpiresAt, setKioskTokenExpiresAt] = useState<Date | null>(null);
  const [kioskTokenUsage, setKioskTokenUsage] = useState<{ usageCount: number; maxUses: number } | null>(null);
  const [kioskTokenError, setKioskTokenError] = useState<string | null>(null);
  const [isGeneratingKioskToken, setIsGeneratingKioskToken] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  const [reportFilters, setReportFilters] = useState<ReportFilters>(DEFAULT_REPORT_FILTERS);
  const [userHistory, setUserHistory] = useState<Test[]>([]);
  const [userHistoryTotal, setUserHistoryTotal] = useState<number | null>(null);
  const [userHistoryLoading, setUserHistoryLoading] = useState(false);
  const [userHistoryLoadingMore, setUserHistoryLoadingMore] = useState(false);
  const [userHistoryError, setUserHistoryError] = useState<string | null>(null);

  const isSuperAdmin = userRole === 'superadmin' || user?.role === 'superadmin';

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.id) return;
      setLoading(true);
      try {
        setDataError(null);
        const results = await Promise.allSettled([
          loadCompanyUsers(),
          loadCompanyAnalytics(),
          loadCompanyTestHistory(),
          loadReactionTimeTrend(),
        ]);
        if (results.some((result) => result.status === 'rejected')) {
          setDataError('Some company data could not be loaded. Retry to check for updates.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.id, reloadCount]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadCompanyUsers = async () => {
    if (!user?.id) return;
    try {
      const response = await apiService.getCompanyUsers(user.id);
      if (response.success && response.data) {
        const usersWithStats: CompanyUser[] = (response.data as any[]).map((apiUser) => ({
          id: apiUser.id,
          name: apiUser.name,
          email: apiUser.email,
          role: apiUser.role.toLowerCase() as 'admin' | 'superadmin',
          companyId: apiUser.companyId,
          createdAt: new Date(apiUser.createdAt),
          updatedAt: new Date(apiUser.updatedAt),
          testCount: apiUser.testCount || 0,
          lastTestDate: apiUser.lastTestDate ? new Date(apiUser.lastTestDate) : undefined,
          averagePerformance: apiUser.averagePerformance || {
            reactionTime: 0,
            lapseRate: 0,
          },
        }));
        setCompanyUsers(usersWithStats);
        return usersWithStats;
      }
      throw new Error(response.error?.message || 'Failed to load company users');
    } catch (error) {
      console.error('Error loading company users:', error);
      setCompanyUsers([]);
      throw error;
    }
  };

  const loadCompanyAnalytics = async () => {
    if (!user?.id) return;
    try {
      const response = await apiService.getCompanyAnalytics(user.id);
      if (response.success && response.data) {
        setCompanyAnalytics(response.data);
        if (response.data.companyId === 'company-1') {
          setCompanyName('Acme Corporation');
        } else if (response.data.companyId === 'company-2') {
          setCompanyName('Tech Solutions Inc');
        } else {
          setCompanyName('Company Dashboard');
        }
        return response.data;
      }
      throw new Error(response.error?.message || 'Failed to load company analytics');
    } catch (error) {
      console.error('Error loading company analytics:', error);
      setCompanyAnalytics(null);
      throw error;
    }
  };

  const loadCompanyTestHistory = async () => {
    if (!user?.companyId) return;
    try {
      const response = await apiService.getTestSummaries({
        limit: 1000
      });
      if (response.success && response.data) {
        const payload = response.data as any;
        const testsData = Array.isArray(payload) ? payload : payload.tests || [];
        const completed = (testsData as any[])
          .filter((test) => test.results)
          .sort((a, b) => new Date(b.testDate).getTime() - new Date(a.testDate).getTime());
        setHistoryTests(completed);
        return completed;
      }
      throw new Error(response.error?.message || 'Failed to load test history');
    } catch (error) {
      console.error('Error loading test history:', error);
      setHistoryTests([]);
      throw error;
    }
  };

  const loadReactionTimeTrend = async () => {
    try {
      const { startDate, endDate } = getLastYearDateRange();
      const tests = await apiService.getAllTestSummaries({
        startDate: `${startDate}T00:00:00`,
        endDate: `${endDate}T23:59:59.999`,
      });
      const completed = tests.filter((test) => test.results);
      setTrendTests(completed);
      return completed;
    } catch (error) {
      console.error('Error loading reaction-time trend:', error);
      setTrendTests([]);
      throw error;
    }
  };

  const handleAddUser = async (userData: {
    name: string;
    email: string;
    password: string;
    role: 'ADMIN';
    companyCode?: string;
    companyName?: string;
  }) => {
    if (!user?.id) {
      const message = 'Unable to add admin: your session is missing a user ID. Please sign in again.';
      showNotification(message, 'error');
      throw new Error(message);
    }
    try {
      const response = await apiService.createCompanyUser(user.id, userData);
      if (!response.success) throw new Error(response.error?.message || 'Failed to add admin');
      showNotification(`Admin ${userData.name} added successfully!`, 'success');
      try {
        await loadCompanyUsers();
      } catch (refreshError) {
        console.error('Admin created but failed to refresh the list:', refreshError);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      const { userMessage, message } = error as { userMessage?: string; message?: string };
      showNotification(userMessage || message || 'Failed to add admin. Please try again.', 'error');
      throw error;
    }
  };

  const handleEditUser = async (userId: string, userData: { name: string; email: string; role: 'ADMIN' }) => {
    if (!user?.id) return;
    try {
      const response = await apiService.updateCompanyUser(user.id, userId, userData);
      if (!response.success) throw new Error(response.error?.message || 'Failed to update admin');
      await loadCompanyUsers();
      showNotification('Admin updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('Failed to update admin. Please try again.', 'error');
      throw error;
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!user?.id) return;
    try {
      const response = await apiService.deleteCompanyUser(user.id, userId);
      if (!response.success) throw new Error(response.error?.message || 'Failed to delete admin');
      await loadCompanyUsers();
      showNotification('Admin deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Failed to delete admin. Please try again.', 'error');
      throw error;
    }
  };

  const loadUserHistoryPage = async (targetUser: CompanyUser, offset: number, append: boolean) => {
    if (!user?.id) return;
    if (append) {
      setUserHistoryLoadingMore(true);
    } else {
      setUserHistoryLoading(true);
    }
    try {
      setUserHistoryError(null);
      const response = await apiService.getUserTestHistory(user.id, targetUser.id, {
        limit: HISTORY_PAGE_SIZE,
        offset,
        includeCount: offset === 0,
      });
      if (response.success && response.data) {
        const page = response.data.tests || [];
        setUserHistory((previous) => (append ? [...previous, ...page] : page));
        if (typeof response.data.total === 'number') {
          setUserHistoryTotal(response.data.total);
        } else if (!append) {
          setUserHistoryTotal(page.length < HISTORY_PAGE_SIZE ? page.length : null);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to load test history');
      }
    } catch (error) {
      console.error('Error loading user history:', error);
      setUserHistoryError('Failed to load test history. Please try again.');
      if (!append) setUserHistory([]);
    } finally {
      if (append) {
        setUserHistoryLoadingMore(false);
      } else {
        setUserHistoryLoading(false);
      }
    }
  };

  const handleViewHistory = (targetUser: CompanyUser) => {
    setSelectedUser(targetUser);
    setUserHistory([]);
    setUserHistoryTotal(null);
    setUserHistoryError(null);
    void loadUserHistoryPage(targetUser, 0, false);
  };

  const closeUserHistory = () => {
    setSelectedUser(null);
    setUserHistory([]);
    setUserHistoryTotal(null);
    setUserHistoryError(null);
    setUserHistoryLoading(false);
    setUserHistoryLoadingMore(false);
  };

  const hasMoreHistory =
    userHistoryTotal !== null
      ? userHistory.length < userHistoryTotal
      : userHistory.length > 0 && userHistory.length % HISTORY_PAGE_SIZE === 0;

  const handleExportCSV = async () => {
    if (!user?.companyId) {
      showNotification('Company ID not found. Unable to export.', 'error');
      return;
    }

    try {
      const response = await apiService.getAllTests({
        limit: 5000,
        includeRaw: true,
        status: 'COMPLETED',
        ...(reportFilters.testType !== 'ALL' ? { testType: reportFilters.testType } : {}),
        ...(reportFilters.startDate ? { startDate: `${reportFilters.startDate}T00:00:00` } : {}),
        ...(reportFilters.endDate ? { endDate: `${reportFilters.endDate}T23:59:59` } : {}),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to load tests');
      }

      const payload = response.data as any;
      const allTests: any[] = Array.isArray(payload) ? payload : payload.tests || [];
      const tests = allTests
        .filter(
          (test) =>
            test.results &&
            (test.user?.companyId === user.companyId || test.employee?.companyId === user.companyId)
        )
        .sort((a, b) => new Date(b.testDate).getTime() - new Date(a.testDate).getTime())
        .map((test) => {
          const results = test.results || {};
          const employeeIdentifier = test.employee?.employeeId || test.employeeId || '';
          const rawReactionTimes: number[] = Array.isArray(results.rawReactionTimes) ? results.rawReactionTimes : [];
          const meanInverseReactionTime = rawReactionTimes.length
            ? rawReactionTimes.reduce((sum, value) => sum + (value > 0 ? 1 / value : 0), 0) / rawReactionTimes.length
            : '';
          return {
            testId: test.id,
            testDate: test.testDate,
            employeeId: employeeIdentifier,
            department: test.department || '',
            workShift: test.workShift || '',
            testType: formatTestType(test.testType),
            lastSleepDuration: test.lastSleepDuration ?? '',
            sleepQuality: test.sleepQuality ?? '',
            sleepinessLevel: test.sleepinessLevel ?? '',
            averageReactionTime: results.averageReactionTime ?? '',
            medianReactionTime: results.medianReactionTime ?? '',
            majorLapses: results.majorLapses ?? '',
            minorLapses: results.minorLapses ?? '',
            totalLapses: results.totalLapses ?? '',
            totalFalseStarts: results.totalFalseStarts ?? '',
            accuracy: results.accuracy ?? '',
            testDurationSeconds: results.testDuration ?? '',
            totalResponses: results.totalResponses ?? '',
            rawReactionTimes: rawReactionTimes.length ? rawReactionTimes.join(' ') : '',
            fastestTenMean: results.fastestTenMean ?? '',
            slowestTenMean: results.slowestTenMean ?? '',
            meanInverseReactionTime,
          };
        });

      if (!tests.length) {
        showNotification('No completed tests found to export for the selected filters.', 'error');
        return;
      }

      const headers = [
        'Test ID',
        'Employee ID',
        'Department',
        'Work Shift',
        'Test Type',
        'Test Date',
        'Test Time',
        'Test Duration (sec)',
        'Last Sleep Duration (hrs)',
        'Sleep Quality',
        'Sleepiness Level',
        'Raw Reaction Times (ms)',
        'Total Responses',
        'Major Lapses',
        'Minor Lapses',
        'Mean Reaction Time (ms)',
        'Median Reaction Time (ms)',
        'Mean (1/Reaction Time)',
        'Fastest 10% Reaction Time (ms)',
        'Slowest 10% Reaction Time (ms)',
      ];

      const csvContent = [
        headers.join(','),
        ...tests.map((row) =>
          [
            `"${row.testId}"`,
            `"${row.employeeId}"`,
            `"${row.department}"`,
            `"${row.workShift}"`,
            `"${row.testType}"`,
            `"${new Date(row.testDate).toLocaleDateString()}"`,
            `"${new Date(row.testDate).toLocaleTimeString()}"`,
            row.testDurationSeconds,
            row.lastSleepDuration,
            row.sleepQuality,
            row.sleepinessLevel,
            `"${row.rawReactionTimes}"`,
            row.totalResponses,
            row.majorLapses,
            row.minorLapses,
            row.averageReactionTime,
            row.medianReactionTime,
            row.meanInverseReactionTime !== '' ? Number(row.meanInverseReactionTime).toFixed(6) : '',
            row.fastestTenMean,
            row.slowestTenMean,
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `company-tests-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showNotification('CSV report exported successfully!', 'success');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showNotification('Failed to export CSV. Please try again.', 'error');
    }
  };

  const handleGenerateKioskToken = async () => {
    if (!user?.id) return;
    setIsGeneratingKioskToken(true);
    setKioskTokenError(null);
    try {
      const response = await apiService.getKioskToken(user.id);
      if (response.success && response.data) {
        setKioskToken(response.data.token);
        const parsedExpiry = new Date(response.data.expiresAt);
        const isParsedValid = !Number.isNaN(parsedExpiry.getTime());
        const isMidnight =
          isParsedValid &&
          parsedExpiry.getHours() === 0 &&
          parsedExpiry.getMinutes() === 0 &&
          parsedExpiry.getSeconds() === 0;
        if (isParsedValid && !isMidnight) {
          setKioskTokenExpiresAt(parsedExpiry);
        } else {
          const fallbackBase = response.timestamp ? new Date(response.timestamp) : new Date();
          if (!Number.isNaN(fallbackBase.getTime())) {
            setKioskTokenExpiresAt(new Date(fallbackBase.getTime() + 8 * 60 * 60 * 1000));
          } else {
            setKioskTokenExpiresAt(null);
          }
        }
        setKioskTokenUsage({
          usageCount: response.data.usageCount ?? 0,
          maxUses: response.data.maxUses ?? 100
        });
      } else {
        setKioskTokenError(response.error?.message || 'Failed to generate token');
      }
    } catch (error) {
      console.error('Error generating kiosk token:', error);
      setKioskTokenError('Failed to generate token');
    } finally {
      setIsGeneratingKioskToken(false);
    }
  };

  const testResults = useMemo(() => historyTests.filter((test) => test.results) as Array<Test & { results: TestResults }>, [historyTests]);

  const metrics = useMemo(() => {
    const avgValues = testResults.map((test) => test.results.averageReactionTime).filter(Boolean);
    const medianValues = testResults
      .map((test) => test.results.medianReactionTime ?? test.results.averageReactionTime)
      .filter(Boolean);

    const averageRT = companyAnalytics?.averagePerformance.reactionTime ?? (avgValues.length ? avgValues.reduce((sum, value) => sum + value, 0) / avgValues.length : 0);
    const medianRT = medianValues.length ? calculateMedian(medianValues) : averageRT;

    return {
      totalTests: companyAnalytics?.totalTests ?? testResults.length,
      averageRT,
      medianRT,
    };
  }, [companyAnalytics, testResults]);

  const chartData = useMemo(() => {
    return toDailyAverages(
      trendTests.map((test) => ({
        date: test.testDate,
        average: test.results?.averageReactionTime ?? null,
        median: test.results?.medianReactionTime ?? null,
      }))
    ).map((point) => ({
      label: point.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      average: point.average,
      median: point.median,
    }));
  }, [trendTests]);

  const latestTests = useMemo(() => {
    return testResults.slice(0, 6).map((test) => {
      // getTestSummaries returns the human-readable identifier on the nested
      // employee object; the top-level employeeId is the internal FK — never
      // display the FK to users.
      const employeeName = test.employee?.name || test.employee?.employeeId || 'Employee';
      return {
        id: test.id,
        employeeName,
        department: test.department || '-',
        averageRT: test.results.averageReactionTime,
        totalLapses: test.results.totalLapses ?? 0,
      };
    });
  }, [testResults]);

  const shiftBreakdown = useMemo(() => {
    const shiftColors = {
      MORNING: '#F0C050',
      EVENING: '#93A0C4',
      NIGHT: '#B8892E',
    } as const;

    if (companyAnalytics?.shiftBreakdown?.length) {
      return companyAnalytics.shiftBreakdown.map((shift) => ({
        label: shift.shift.charAt(0) + shift.shift.slice(1).toLowerCase(),
        value: shift.testCount,
        color: shiftColors[shift.shift as keyof typeof shiftColors] || '#A3A3A3',
      }));
    }

    const counts = testResults.reduce(
      (acc, test) => {
        const shift = test.workShift || 'UNASSIGNED';
        acc[shift] = (acc[shift] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(counts).map(([shift, value]) => ({
      label: shift.charAt(0) + shift.slice(1).toLowerCase(),
      value,
      color: shiftColors[shift as keyof typeof shiftColors] || '#A3A3A3',
    }));
  }, [companyAnalytics, testResults]);

  const departmentHistory = useMemo(() => {
    const breakdown = companyAnalytics?.departmentBreakdown;
    if (breakdown && breakdown.length) {
      return breakdown.map((dept) => {
        const lastTest = testResults.find((test) => test.department === dept.department);
        return {
          id: dept.department,
          department: dept.department,
          testsTaken: dept.testCount,
          lastUpdated: lastTest ? toDate(lastTest.testDate) : undefined,
        };
      });
    }

    const departmentMap = testResults.reduce((acc, test) => {
      const department = test.department || 'Unassigned';
      const entry = acc.get(department) || { count: 0, lastUpdated: undefined as Date | undefined };
      entry.count += 1;
      const testDate = toDate(test.testDate);
      if (testDate && (!entry.lastUpdated || testDate > entry.lastUpdated)) {
        entry.lastUpdated = testDate;
      }
      acc.set(department, entry);
      return acc;
    }, new Map<string, { count: number; lastUpdated?: Date }>());

    return Array.from(departmentMap.entries()).map(([department, stats]) => ({
      id: department,
      department,
      testsTaken: stats.count,
      lastUpdated: stats.lastUpdated,
    }));
  }, [companyAnalytics, testResults]);

  const activeTab = useMemo<DashboardTab>(() => {
    switch (location.pathname) {
      case ROUTES.ADMIN.ANALYTICS:
        return 'Analytics';
      case ROUTES.ADMIN.CONFIGURATION:
        return 'People';
      case ROUTES.ADMIN.REPORTS:
        return 'Reports';
      case ROUTES.ADMIN.PROFILE:
        return 'Profile & Access';
      case ROUTES.ADMIN.DASHBOARD:
      default:
        return 'Dashboard';
    }
  }, [location.pathname]);

  const activeHeader = useMemo(() => {
    switch (activeTab) {
      case 'Dashboard':
        return 'Company Dashboard';
      case 'Analytics':
        return 'Analytics';
      case 'People':
        return 'People';
      case 'Reports':
        return 'Reports';
      case 'Take a Test':
        return 'Take a Test';
      case 'Profile & Access':
        return 'Profile & Access';
      default:
        return 'Company Dashboard';
    }
  }, [activeTab]);

  const handleTabChange = (tab: DashboardTab) => {
    setIsMobileNavOpen(false);
    switch (tab) {
      case 'Dashboard':
        navigate(ROUTES.ADMIN.DASHBOARD);
        break;
      case 'Analytics':
        navigate(ROUTES.ADMIN.ANALYTICS);
        break;
      case 'People':
        navigate(ROUTES.ADMIN.CONFIGURATION);
        break;
      case 'Reports':
        navigate(ROUTES.ADMIN.REPORTS);
        break;
      case 'Take a Test':
        navigate(ROUTES.ADMIN.TEST);
        break;
      case 'Profile & Access':
        navigate(ROUTES.ADMIN.PROFILE);
        break;
      default:
        navigate(ROUTES.ADMIN.DASHBOARD);
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
      <a href="#admin-main-content" className="sr-only z-50 rounded bg-navy-900 p-3 text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4">
        Skip to dashboard content
      </a>
      <aside
        aria-label="Admin sidebar"
        className={`fixed left-0 top-0 bottom-0 bg-navy-800 border-r border-navy-600 shadow-sm hidden md:flex flex-col p-4 transition-all duration-300 ${
          isNavCollapsed ? 'w-20' : 'w-72'
        }`}
      >
        <div className={`flex items-center ${isNavCollapsed ? 'justify-center' : 'justify-between'} mb-6`}>
          <div className={`flex items-center gap-2 ${isNavCollapsed ? 'justify-center w-full' : ''}`}>
            <Logo size="sm" />
          </div>
        </div>

        <Navigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="!bg-transparent !p-0 !rounded-none !shadow-none !border-0 !w-full"
          sticky={false}
          enableCollapse={true}
          collapsed={isNavCollapsed}
          onCollapsedChange={setIsNavCollapsed}
        />

        <div className="mt-auto pt-6 border-t border-navy-700">
          {user && (
            <div className={`flex items-center ${isNavCollapsed ? 'justify-center' : 'justify-between'} gap-3`}>
              {!isNavCollapsed && (
                <div className="text-sm text-navy-200">
                  <div className="font-semibold text-white">{user.name}</div>
                  <div className="text-xs capitalize">{user.role}</div>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={logout} aria-label="Log out" className={isNavCollapsed ? 'px-2' : ''}>
                {isNavCollapsed ? '⎋' : 'Logout'}
              </Button>
            </div>
          )}
        </div>
      </aside>

      <div className="md:hidden sticky top-0 z-30 border-b border-navy-700 bg-navy-900/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between">
          <Logo size="xs" />
          <Button
            variant="outline"
            size="icon"
            aria-label={isMobileNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={isMobileNavOpen}
            aria-controls={isMobileNavOpen ? 'mobile-admin-navigation' : undefined}
            onClick={() => setIsMobileNavOpen((open) => !open)}
          >
            {isMobileNavOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </Button>
        </div>
        {isMobileNavOpen && (
          <div id="mobile-admin-navigation" className="pt-4">
            <Navigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
              className="!w-full !border-0 !bg-transparent !p-0 !shadow-none"
              sticky={false}
              enableCollapse={false}
            />
            <Button variant="outline" onClick={logout} className="mt-3 w-full">
              Log out
            </Button>
          </div>
        )}
      </div>

      <main id="admin-main-content" tabIndex={-1} className={`min-h-screen transition-all duration-300 ml-0 ${isNavCollapsed ? 'md:ml-20' : 'md:ml-72'} p-4 sm:p-6`}>
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">{activeHeader}</h1>
              <p className="text-sm text-navy-200 mt-1">{companyName}</p>
            </div>
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate(ROUTES.ADMIN.TEST)} className="bg-gold-500 text-navy-950 hover:bg-gold-400">
                Start a test
              </Button>
            </div>
          </div>

          {dataError && (
            <div role="alert" className="mb-6 flex flex-col gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200 sm:flex-row sm:items-center sm:justify-between">
              <span>{dataError}</span>
              <Button variant="outline" size="sm" onClick={() => setReloadCount((count) => count + 1)}>
                Retry
              </Button>
            </div>
          )}

          <div>
            {loading && (
              <div className="bg-navy-800 rounded-2xl p-12 text-center text-navy-300">Loading dashboard data...</div>
            )}
            {!loading && activeTab === 'Dashboard' && (
              <DashboardOverview
                metrics={metrics}
                chartData={chartData}
                latestTests={latestTests}
              />
            )}
            {!loading && activeTab === 'Analytics' && (
              <div className="space-y-6">
                <AnalyticsResultsChart tests={historyTests} />
                <AnalyticsHeatmap tests={historyTests} />
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <EmployeeDonutChart segments={shiftBreakdown} title="Tests by shift" />
                  <DepartmentTestHistory items={departmentHistory} />
                </div>
              </div>
            )}
            {!loading && activeTab === 'People' && (
              <CompanyUserList
                users={companyUsers}
                onAddUser={handleAddUser}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
                onViewHistory={handleViewHistory}
                loading={loading}
                isSuperAdmin={isSuperAdmin}
              />
            )}
            {!loading && activeTab === 'Reports' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white">Company Test Reports</h2>
                  <Button onClick={handleExportCSV} className="bg-gold-500 hover:bg-gold-600">
                    Export CSV
                  </Button>
                </div>
                <TestHistory
                  showUserInfo={true}
                  paginate={true}
                  pageSize={50}
                  status="COMPLETED"
                  filters={reportFilters}
                  onFiltersChange={setReportFilters}
                />
              </div>
            )}
            {!loading && activeTab === 'Profile & Access' && (
              <div className="bg-navy-800 rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-white">Profile</h2>
                </div>
                <div className="space-y-8">
                  {user ? <UserProfile user={user} size="lg" showLogout={false} /> : <p className="text-navy-200">No user data available.</p>}

                  <div className="border-t border-navy-700 pt-6">
                    <h3 className="text-lg font-semibold text-white mb-3">Kiosk Access Token</h3>
                    <p className="text-sm text-navy-200 mb-4">
                      Generate a 6-character token for kiosk test access. The token identifies your company and is valid
                      for 8 hours or 100 test starts, whichever comes first.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <Button
                        onClick={handleGenerateKioskToken}
                        className="bg-gold-500 hover:bg-gold-600"
                        disabled={isGeneratingKioskToken}
                      >
                        {isGeneratingKioskToken ? 'Generating...' : 'Generate Token'}
                      </Button>
                      {kioskToken && (
                        <div className="flex items-center gap-3 bg-navy-800 border border-navy-600 rounded-lg px-4 py-2">
                          <span className="text-sm font-semibold tracking-[0.4em] text-gold-400">{kioskToken}</span>
                          <button
                            className="text-xs text-gold-400 hover:text-gold-300 underline"
                            onClick={() => navigator.clipboard?.writeText(kioskToken)}
                          >
                            Copy
                          </button>
                        </div>
                      )}
                    </div>
                    {kioskTokenExpiresAt && (
                      <p className="text-xs text-navy-300 mt-2">
                        Expires at{' '}
                        {kioskTokenExpiresAt.toLocaleString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          month: 'short',
                          day: '2-digit',
                        })}
                      </p>
                    )}
                    {kioskTokenUsage && (
                      <p className="text-xs text-navy-300 mt-1">
                        Uses: {kioskTokenUsage.usageCount} / {kioskTokenUsage.maxUses}
                      </p>
                    )}
                    {kioskTokenError && (
                      <p className="text-sm text-red-400 mt-2">{kioskTokenError}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-history-title"
        >
          <div className="bg-navy-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto relative">
            <button
              type="button"
              onClick={closeUserHistory}
              aria-label="Close test history"
              className="absolute top-4 right-4 rounded-lg p-1.5 text-navy-200 hover:bg-navy-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-gold-500"
            >
              <X size={20} aria-hidden="true" />
            </button>
            <h3 id="user-history-title" className="text-lg font-semibold mb-4 pr-8">
              Test History - {selectedUser.name}
              {userHistoryTotal !== null && (
                <span className="ml-2 text-sm font-normal text-navy-300">({userHistoryTotal} tests)</span>
              )}
            </h3>

            {userHistoryError && (
              <div
                role="alert"
                className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200"
              >
                <span>{userHistoryError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadUserHistoryPage(selectedUser, userHistory.length, userHistory.length > 0)}
                >
                  Retry
                </Button>
              </div>
            )}

            {userHistoryLoading ? (
              <div className="py-12 text-center text-navy-300">
                <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-gold-500"></div>
                Loading test history...
              </div>
            ) : userHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-navy-700">
                  <thead>
                    <tr className="bg-navy-900">
                      <th className="border border-navy-700 px-4 py-2 text-left">Date</th>
                      <th className="border border-navy-700 px-4 py-2 text-left">Test Type</th>
                      <th className="border border-navy-700 px-4 py-2 text-left">Avg RT (ms)</th>
                      <th className="border border-navy-700 px-4 py-2 text-left">Lapses</th>
                      <th className="border border-navy-700 px-4 py-2 text-left">False Starts</th>
                      <th className="border border-navy-700 px-4 py-2 text-left">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userHistory.map((test, index) => (
                      <tr key={index} className="hover:bg-navy-900">
                        <td className="border border-navy-700 px-4 py-2">
                          {formatDate(new Date(test.testDate))}
                        </td>
                        <td className="border border-navy-700 px-4 py-2">
                          {formatTestType((test as Test).testType)}
                        </td>
                        <td className="border border-navy-700 px-4 py-2">
                          <span className="text-white">
                            {Math.round(test.results?.averageReactionTime || 0)}
                          </span>
                        </td>
                        <td className="border border-navy-700 px-4 py-2">
                          {test.results?.totalLapses || 0}
                        </td>
                        <td className="border border-navy-700 px-4 py-2">
                          {test.results?.totalFalseStarts || 0}
                        </td>
                        <td className="border border-navy-700 px-4 py-2">
                          {test.results?.accuracy ? `${test.results.accuracy.toFixed(1)}%` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !userHistoryError && (
                <p className="text-navy-300 text-center py-8">No test history available for this user.</p>
              )
            )}

            {!userHistoryLoading && userHistory.length > 0 && hasMoreHistory && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => loadUserHistoryPage(selectedUser, userHistory.length, true)}
                  disabled={userHistoryLoadingMore}
                >
                  {userHistoryLoadingMore ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
